import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ITimelineMarker, IVideoMetadata } from '@epicuro-next/ataraxia/models';
import { selectIsConfigStripOpen } from '@epicuro-next/state/config-strip-state';
import {
  selectEpicShowProducts,
  selectOpenedListProducts,
} from '@epicuro-next/state/product-list-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import { Media } from '@epicuro-next/models/engagement';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IEpicShowMetadata } from '../../interfaces/epic-show-metadata';
import { EpicShowService } from '../../service/epic-show.service';
import { createMarkers } from '../../utils/create-markers';

@Component({
  selector: 'epc-epic-show-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './epic-show-player.component.html',
  styleUrls: ['./epic-show-player.component.scss'],
  standalone: false,
})
export class EpicShowPlayerComponent implements OnInit, OnChanges, OnDestroy {
  public videoCurrentTime = 0;
  public videoDuration = 0;
  public videoVolume = 0;
  public defaultVideoDuration?: number;
  public defaultVideoVolumne?: number;
  public videoMarkers: ITimelineMarker[];
  public activeProducts$$ = new BehaviorSubject<Product[]>([]);
  public playVideo$$ = new BehaviorSubject(true);

  private products: Map<string, Product> = new Map();
  private destroy$: Subject<void> = new Subject();

  @Input()
  public listId: string;

  @Input()
  public mediaMetadata: IEpicShowMetadata;

  @Input()
  public media: Media;

  @Input()
  public enableFullScreen = false;

  @Input()
  public isOwner: boolean;

  @Output()
  public timeUpdate: EventEmitter<number> = new EventEmitter();

  @Output()
  public loadedMetadata: EventEmitter<IVideoMetadata> = new EventEmitter();

  @Output()
  public epicShowFullScreenChanged = new EventEmitter();

  public isConfigStripOpen$ = this.store.pipe(select(selectIsConfigStripOpen));

  constructor(private store: Store, private epicShowService: EpicShowService) {}

  public ngOnInit() {
    this.subscribeProducts();
    this.parseMetadata(this.mediaMetadata);
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const mediaMetadata = changes.mediaMetadata;
    const listIdChange = changes.listId;
    const mediaChange = changes.media;

    if (
      listIdChange &&
      !listIdChange.firstChange &&
      listIdChange.currentValue !== listIdChange.previousValue
    ) {
      this.destroy$.next();
      this.products.clear();
      this.subscribeProducts();
    }

    if (mediaMetadata && !mediaMetadata.firstChange) {
      this.parseMetadata(mediaMetadata.currentValue);
    }

    if (mediaChange) {
      this.setDefaultVideoAttributes();
    }
  }

  public onLoadedMetadata(data: IVideoMetadata) {
    this.videoDuration = data.duration;

    this.loadedMetadata.emit(data);
  }

  public onTimeUpdate(time: number) {
    this.videoCurrentTime = time;

    this.timeUpdate.emit(time);
    this.activeProducts$$.next(this.findActiveProducts(time));

    this.upsertVideoConsumptionAttributes();
  }

  public onVolumeUpdate(value: number) {
    this.videoVolume = value;

    this.upsertVideoConsumptionAttributes();
  }

  public onFullScreenClicked() {
    this.epicShowFullScreenChanged.emit();
  }

  public upsertVideoConsumptionAttributes() {
    const url = this.media?.url;
    if (!url || this.isOwner) return;

    this.epicShowService.upsertVideoConsumptionAttributes({
      videoUrl: url,
      timestamp: this.videoCurrentTime,
      volume: this.videoVolume,
    });
  }

  private setDefaultVideoAttributes() {
    if (!this.isOwner) return;

    const key = this.media.url;
    const { volume, timestamp } =
      this.epicShowService.getVideoConsumptionAttributes(key) ?? {};

    this.defaultVideoVolumne = volume;
    this.defaultVideoDuration = timestamp;
  }

  private findActiveProducts(time: number): Product[] {
    const productIds: string[] = [];
    for (const productId of Object.keys(this.mediaMetadata?.products ?? {})) {
      for (const { start, end } of this.mediaMetadata.products[productId]) {
        if (start && end && start <= time && time <= end) {
          productIds.push(productId);
        }
      }
    }

    return productIds
      .filter((id, i) => productIds.indexOf(id) === i) // remove dupes
      .map((id) => this.products.get(id))
      .filter(isNonNullish);
  }

  private parseMetadata(data: IEpicShowMetadata) {
    if (!this.products.size) {
      return;
    }

    this.videoMarkers = createMarkers(this.products, data);
  }

  private subscribeProducts() {
    merge(
      this.store.pipe(select(selectEpicShowProducts)),
      this.store.pipe(select(selectOpenedListProducts)),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((products: Product[]) => {
        products
          .filter((item) => !this.products.has(item.productId))
          .forEach((item) => {
            this.products.set(item.productId, {
              ...item,
              isAvailable: item.isAvailable ?? true,
            });
          });
        this.parseMetadata(this.mediaMetadata);
      });
  }
}
